import { FormEvent, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, KeyRound, ShieldCheck } from "lucide-react";

type LinkInfo = {
  valid: boolean;
  purpose: "invite" | "reset";
  fullName: string;
};

async function api(path: string, options?: RequestInit) {
  const response = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options?.headers ?? {}) },
    ...options,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error ?? "Une erreur est survenue");
  return body;
}

export default function TeacherActivationApp() {
  const token = decodeURIComponent(window.location.pathname.split("/").filter(Boolean)[1] ?? "");
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    void api(`/api/auth/password-link?token=${encodeURIComponent(token)}`)
      .then((result) => { if (active) setInfo(result); })
      .catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : "Lien invalide"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (password.length < 10) return setError("Le mot de passe doit contenir au moins 10 caractères.");
    if (password !== confirmation) return setError("Les deux mots de passe sont différents.");
    setBusy(true);
    try {
      await api("/api/auth/password-link", { method: "POST", body: JSON.stringify({ token, newPassword: password }) });
      setDone(true);
      setPassword("");
      setConfirmation("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <main className="activation-shell"><section className="activation-card"><div className="activation-logo"><KeyRound size={28} /></div><h1>Vérification du lien…</h1></section></main>;

  if (done) return <main className="activation-shell"><section className="activation-card activation-success"><div className="activation-logo"><CheckCircle2 size={30} /></div><span className="activation-kicker">CompetenceO</span><h1>Mot de passe enregistré</h1><p>Ton accès est prêt. Tu peux maintenant te connecter à CompetenceO avec ton adresse e-mail.</p><a className="activation-primary" href="/">Se connecter</a></section></main>;

  if (!info || error && !password && !confirmation) return <main className="activation-shell"><section className="activation-card activation-error"><div className="activation-logo"><AlertTriangle size={30} /></div><span className="activation-kicker">CompetenceO</span><h1>Lien indisponible</h1><p>{error || "Ce lien est invalide ou a expiré."}</p><a className="activation-secondary" href="/">Retour à CompetenceO</a></section></main>;

  return <main className="activation-shell">
    <section className="activation-card">
      <div className="activation-logo"><KeyRound size={29} /></div>
      <span className="activation-kicker">CompetenceO</span>
      <h1>{info.purpose === "invite" ? "Créer ton mot de passe" : "Choisir un nouveau mot de passe"}</h1>
      <p>Bonjour <strong>{info.fullName}</strong>. {info.purpose === "invite" ? "Ton compte professeur a été créé." : "Une réinitialisation de ton mot de passe a été demandée."}</p>
      <form className="activation-form" onSubmit={submit}>
        <label><span>Nouveau mot de passe</span><input type="password" autoComplete="new-password" minLength={10} value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
        <label><span>Confirmer le mot de passe</span><input type="password" autoComplete="new-password" minLength={10} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required /></label>
        {error && <p className="activation-form-error">{error}</p>}
        <button className="activation-primary" disabled={busy}><ShieldCheck size={18} /> {busy ? "Enregistrement…" : "Enregistrer mon mot de passe"}</button>
      </form>
      <small>Le lien est personnel, valable 48 h et utilisable une seule fois.</small>
    </section>
  </main>;
}
